import AutomaticTestInstructions from '../../components/AutomaticTestInstructions'
import { automaticTestInstructions } from '../../utils/testInstructions'
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { driveAPI } from '../../services/api'

interface DriveForm {
  title: string
  description: string
  startDate: string
  endDate: string
  maxCandidates: number
  testDuration: number
  instructions: string
  negativeMarking: number
  collegeType: 'engineering' | 'management' | ''
  tier: 'tier1' | 'tier2' | ''
  role: 'ASE' | 'Cloud Support Engineer' | 'Pre-Sales' | 'AIQ Test' | ''
}

interface Drive {
  id: string
  title: string
  description: string
  startDate: string
  endDate: string
  maxCandidates: number
  registeredCandidates: number
  testDuration: number
  status: 'upcoming' | 'active' | 'completed' | 'cancelled'
  instructions: string
  createdAt: string
  collegeType: 'engineering' | 'management'
  tier: 'tier1' | 'tier2'
  role?: 'ASE' | 'Cloud Support Engineer' | 'Pre-Sales' | 'AIQ Test'
  registrationLink?: string
  linkedAssessments?: any[]
  registeredCandidatesList?: any[]
  assessment_configs?: any
  drive_metadata?: any
}

const EditDrivePage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [drive, setDrive] = useState<Drive | null>(null)

  const [formData, setFormData] = useState<DriveForm>({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    maxCandidates: 100,
    testDuration: 60,
    instructions: '',
    negativeMarking: 0,
    collegeType: '',
    tier: '',
    role: ''
  })

  useEffect(() => {
    loadDriveData()
  }, [id, location.state])

  const loadDriveData = async () => {
    try {
      // First try to get drive from navigation state
      if (location.state?.drive) {
        const driveData = location.state.drive as Drive
        setDrive(driveData)
        const driveMetadata = driveData.drive_metadata || {}
        const negativeMarking = driveMetadata.negative_marking || 0
        setFormData({
          title: driveData.title,
          description: driveData.description,
          startDate: driveData.startDate.slice(0, 16), // Convert to datetime-local format
          endDate: driveData.endDate.slice(0, 16),
          maxCandidates: driveData.maxCandidates,
          testDuration: driveData.testDuration,
          instructions: driveData.instructions,
          negativeMarking: negativeMarking,
          collegeType: driveData.collegeType,
          tier: driveData.tier,
          role: driveData.role || ''
        })
        setIsLoading(false)
        return
      }

      // Load from API
      if (id) {
        try {
          const response = await driveAPI.getDrive(parseInt(id))
          const apiDrive = response.data
          
          // Transform API response to match frontend interface
          const transformedDrive: Drive = {
            id: apiDrive.id.toString(),
            title: apiDrive.title,
            description: apiDrive.description || '',
            startDate: apiDrive.start_date,
            endDate: apiDrive.end_date,
            maxCandidates: apiDrive.max_candidates,
            registeredCandidates: apiDrive.registered_candidates,
            testDuration: apiDrive.total_duration_minutes,
            status: apiDrive.status as 'upcoming' | 'active' | 'completed' | 'cancelled',
            instructions: apiDrive.instructions || '',
            createdAt: apiDrive.created_at,
            collegeType: apiDrive.college_type as 'engineering' | 'management',
            tier: apiDrive.tier as 'tier1' | 'tier2',
            role: apiDrive.role as 'ASE' | 'Cloud Support Engineer' | 'Pre-Sales' | 'AIQ Test' | undefined,
            registrationLink: apiDrive.registration_link,
            linkedAssessments: apiDrive.linked_assessments || [],
            registeredCandidatesList: apiDrive.registered_candidates_list || [],
            assessment_configs: apiDrive.assessment_configs || {},
            drive_metadata: apiDrive.drive_metadata || {}
          }
          
          setDrive(transformedDrive)
          const driveMetadata = transformedDrive.drive_metadata || {}
          const negativeMarking = driveMetadata.negative_marking || 0
          setFormData({
            title: transformedDrive.title,
            description: transformedDrive.description,
            startDate: transformedDrive.startDate.slice(0, 16),
            endDate: transformedDrive.endDate.slice(0, 16),
            maxCandidates: transformedDrive.maxCandidates,
            testDuration: transformedDrive.testDuration,
            instructions: transformedDrive.instructions,
            negativeMarking: negativeMarking,
            collegeType: transformedDrive.collegeType,
            tier: transformedDrive.tier,
            role: transformedDrive.role || ''
          })
        } catch (apiError) {
          console.error('Error loading drive from API:', apiError)
          // Fallback: load from localStorage
          const savedDrives = localStorage.getItem('recruiterDrives')
          if (savedDrives) {
            const drives = JSON.parse(savedDrives)
            const foundDrive = drives.find((d: Drive) => d.id === id)
            
            if (foundDrive) {
              setDrive(foundDrive)
              const driveMetadata = foundDrive.drive_metadata || {}
              const negativeMarking = driveMetadata.negative_marking || 0
              setFormData({
                title: foundDrive.title,
                description: foundDrive.description,
                startDate: foundDrive.startDate.slice(0, 16),
                endDate: foundDrive.endDate.slice(0, 16),
                maxCandidates: foundDrive.maxCandidates,
                testDuration: foundDrive.testDuration,
                instructions: foundDrive.instructions,
                negativeMarking: negativeMarking,
                collegeType: foundDrive.collegeType,
                tier: foundDrive.tier,
                role: foundDrive.role || ''
              })
            } else {
              toast.error('Drive not found')
              navigate('/recruiter/drives')
            }
          } else {
            toast.error('Drive not found')
            navigate('/recruiter/drives')
          }
        }
      }
    } catch (error) {
      console.error('Error loading drive data:', error)
      toast.error('Failed to load drive data')
      navigate('/recruiter/drives')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: name === 'maxCandidates' || name === 'testDuration' ? parseInt(value) || 0 : name === 'negativeMarking' ? parseFloat(value) || 0 : value,
      // Reset tier and role when college type changes
      ...(name === 'collegeType' && { tier: '', role: '' }),
      // Reset role when tier changes
      ...(name === 'tier' && { role: '' })
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!drive) return

    setIsSubmitting(true)

    try {
      // Validate dates
      const now = new Date()
      const startDate = new Date(formData.startDate)
      const endDate = new Date(formData.endDate)

      // Check if start date is in the past (only for new drives or if changing to past)
      if (startDate <= now && drive.status === 'upcoming') {
        toast.error('Start date must be in the future for upcoming drives')
        setIsSubmitting(false)
        return
      }

      // Check if end date is before start date
      if (endDate <= startDate) {
        toast.error('End date must be after start date')
        setIsSubmitting(false)
        return
      }

      // Validate college type and tier
      if (!formData.collegeType) {
        toast.error('Please select a college type')
        setIsSubmitting(false)
        return
      }

      if (!formData.tier) {
        toast.error('Please select a tier')
        setIsSubmitting(false)
        return
      }

      // Validate role for engineering colleges
      if (formData.collegeType === 'engineering' && !formData.role) {
        toast.error('Please select a role')
        setIsSubmitting(false)
        return
      }

      // Prepare drive data for API
      // IMPORTANT: Send dates WITHOUT timezone conversion to preserve exact time entered
      // datetime-local format: "2025-11-01T15:42" -> send as "2025-11-01T15:42:00" (naive, no timezone)
      const formatDateTimeForAPI = (dateTimeLocal: string): string => {
        // datetime-local gives format: "YYYY-MM-DDTHH:mm"
        // Convert to: "YYYY-MM-DDTHH:mm:00" (add seconds, NO timezone info)
        if (dateTimeLocal && dateTimeLocal.includes('T')) {
          const parts = dateTimeLocal.split('T')
          const datePart = parts[0]
          const timePart = parts[1] || '00:00'
          // Ensure time has seconds
          const timeWithSeconds = timePart.split(':').length === 2 
            ? `${timePart}:00` 
            : timePart
          const formatted = `${datePart}T${timeWithSeconds}`
          console.log(`Frontend: Converting "${dateTimeLocal}" -> "${formatted}" (no timezone, preserving local time)`)
          return formatted
        }
        return dateTimeLocal
      }
      
      const driveData = {
        title: formData.title,
        description: formData.description,
        start_date: formatDateTimeForAPI(formData.startDate),
        end_date: formatDateTimeForAPI(formData.endDate),
        max_candidates: formData.maxCandidates,
        instructions: automaticTestInstructions(drive?.assessment_configs),
        // Keep existing assessment_configs if available
        assessment_configs: drive?.assessment_configs || {},
        drive_metadata: {
          ...(drive?.drive_metadata || {}),
          negative_marking: formData.negativeMarking || 0
        }
      }
      
      console.log('Updating drive via API (dates without timezone, preserving local time):', { id: drive.id, ...driveData })
      
      // Update drive via API
      const response = await driveAPI.updateDrive(parseInt(drive.id), driveData)
      console.log('Drive updated successfully:', response.data)
      
      // Also update localStorage for backward compatibility
      const savedDrives = localStorage.getItem('recruiterDrives')
      if (savedDrives) {
        const drives = JSON.parse(savedDrives)
        const updatedDrives = drives.map((d: Drive) => {
          if (d.id === drive.id) {
            return {
              ...d,
              ...formData,
              role: formData.collegeType === 'engineering' ? formData.role : undefined,
              startDate: new Date(formData.startDate).toISOString(),
              endDate: new Date(formData.endDate).toISOString()
            }
          }
          return d
        })
        localStorage.setItem('recruiterDrives', JSON.stringify(updatedDrives))
      }
      
      toast.success('Drive updated successfully!')
      navigate(`/recruiter/drives/${drive.id}`)
    } catch (error) {
      console.error('Error updating drive:', error)
      toast.error('Failed to update drive. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (drive) {
      navigate(`/recruiter/drives/${drive.id}`)
    } else {
      navigate('/recruiter/drives')
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading drive data...</p>
        </div>
      </div>
    )
  }

  if (!drive) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Drive not found</h2>
          <p className="text-gray-600 mb-6">The drive you're trying to edit doesn't exist.</p>
          <button
            onClick={() => navigate('/recruiter/drives')}
            className="btn btn-primary"
          >
            Back to Drives
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleCancel}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Back to Drive Details
          </button>
          
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg mr-4">
              <PencilIcon className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Drive</h1>
              <p className="text-gray-600 mt-1">Update the details of "{drive.title}"</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Drive Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    required
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Software Engineer Drive 2024"
                  />
                </div>

                <div>
                  <label htmlFor="maxCandidates" className="block text-sm font-medium text-gray-700 mb-2">
                    Max Candidates *
                  </label>
                  <input
                    type="number"
                    id="maxCandidates"
                    name="maxCandidates"
                    required
                    min="1"
                    value={formData.maxCandidates}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* College Type and Tier Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label htmlFor="collegeType" className="block text-sm font-medium text-gray-700 mb-2">
                    College Type *
                  </label>
                  <select
                    id="collegeType"
                    name="collegeType"
                    required
                    value={formData.collegeType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select College Type</option>
                    <option value="engineering">Engineering Colleges</option>
                    <option value="management">Management Colleges</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="tier" className="block text-sm font-medium text-gray-700 mb-2">
                    Tier *
                  </label>
                  <select
                    id="tier"
                    name="tier"
                    required
                    value={formData.tier}
                    onChange={handleInputChange}
                    disabled={!formData.collegeType}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Tier</option>
                    {formData.collegeType === 'engineering' && (
                      <>
                        <option value="tier1">Tier 1 (IITs, NITs and IIITs)</option>
                        <option value="tier2">Tier 2 (Other Engineering Colleges)</option>
                      </>
                    )}
                    {formData.collegeType === 'management' && (
                      <>
                        <option value="tier1">Tier 1 (IIMs & Top MBA Institutes)</option>
                        <option value="tier2">Tier 2 (Other MBA Colleges)</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              {/* Role Selection - Only for Engineering Colleges */}
              {formData.collegeType === 'engineering' && formData.tier && (
                <div className="mt-6">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                    Role *
                  </label>
                  <select
                    id="role"
                    name="role"
                    required
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Role</option>
                    <option value="ASE">ASE (Associate Software Engineer)</option>
                    <option value="Cloud Support Engineer">Cloud Support Engineer</option>
                    <option value="Pre-Sales">Pre-Sales</option>
                    <option value="AIQ Test">AIQ Test</option>
                  </select>
                  {(formData.role === 'Cloud Support Engineer' || formData.role === 'Pre-Sales') && (
                    <p className="mt-2 text-sm text-amber-600">
                      Note: Coding Assessment will be excluded for this role.
                    </p>
                  )}
                </div>
              )}

              {/* Assessment Description */}
              {formData.collegeType && formData.tier && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Assessment Focus:</h4>
                  <p className="text-sm text-blue-800">
                    {formData.collegeType === 'engineering' && formData.tier === 'tier1' && 
                      "Advanced-level assessments to evaluate problem-solving, coding, and analytical capabilities."
                    }
                    {formData.collegeType === 'engineering' && formData.tier === 'tier2' && 
                      "Moderate-level assessments focusing on aptitude, reasoning, and applied technical skills."
                    }
                    {formData.collegeType === 'management' && formData.tier === 'tier1' && 
                      "Case-study based assessments, business simulations, and analytical problem-solving."
                    }
                    {formData.collegeType === 'management' && formData.tier === 'tier2' && 
                      "Aptitude, reasoning, communication, and situational judgment tests."
                    }
                  </p>
                </div>
              )}

              <div className="mt-6">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the drive, requirements, and what candidates can expect..."
                />
              </div>
            </div>

            {/* Schedule */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="datetime-local"
                    id="startDate"
                    name="startDate"
                    required
                    min={drive.status === 'upcoming' ? new Date().toISOString().slice(0, 16) : undefined}
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {drive.status === 'upcoming' && (
                    <p className="text-sm text-gray-500 mt-1">Start date must be in the future for upcoming drives</p>
                  )}
                </div>

                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <input
                    type="datetime-local"
                    id="endDate"
                    name="endDate"
                    required
                    min={formData.startDate || new Date().toISOString().slice(0, 16)}
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4"><AutomaticTestInstructions configs={drive?.assessment_configs} /></div>

            {/* Negative Marking */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Scoring Configuration</h3>
              <div>
                <label htmlFor="negativeMarking" className="block text-sm font-medium text-gray-700 mb-2">
                  Negative Marking (per incorrect answer)
                </label>
                <select
                  id="negativeMarking"
                  name="negativeMarking"
                  value={formData.negativeMarking}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={0}>0 (No negative marking)</option>
                  <option value={0.25}>0.25</option>
                  <option value={0.30}>0.30</option>
                  <option value={0.50}>0.50</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Select the negative marking value. For example, 0.25 means each incorrect answer deducts 0.25 marks.
                  Final Score = w1*((m1-q*(n1-m1))/n1) + w2*((m2-q*(n2-m2))/n2) + w3*((m3-q*(n3-m3))/n3)
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <PencilIcon className="w-4 h-4 mr-2" />
                    Update Drive
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditDrivePage
